import React, { FC, useEffect, useState } from "react";
import { Radio, Button, AutoComplete } from "antd";
import styles from "./disbursement-row.css";
import {
  TaxLabelStatus as taxLabelStatusEnum,
  Disbursement,
  LibraryDisbursement,
} from "../../data-types";
import { PriceInput } from "../input-number";
import { TaxLabel } from "~/gql/graphql";
import { Icons } from "../vpd-icons/icons";

const {
  ["disbursement-container"]: disbursementContainerClassname,
  ["name-input"]: nameInputClassname,
  ["amount-input"]: amountInputClassname,
  ["vat-label"]: vatLabeClassname,
} = styles as any;

type Props = {
  disbursement: Disbursement;
  libraryDisbursements: LibraryDisbursement[];
  onUpdate: (disbursement: Partial<Disbursement>) => void;
  onDelete: (id: number) => void;
  autoFocus?: boolean;
  onAutoFocus?: () => void;
  taxLevelPercent: number;
  taxLabel: TaxLabel;
  currencySymbol: string;
  excludeTax: boolean;
};
export const DisbursementRow: FC<Props> = ({
  disbursement: { id, name, vatStatus: initialTaxLabelStatus, amount: initialAmount },
  libraryDisbursements = [],
  onUpdate,
  onDelete,
  autoFocus = false,
  onAutoFocus,
  taxLevelPercent,
  taxLabel,
  currencySymbol,
  excludeTax,
}) => {
  const [taxLabelStatus, setVatStatus] = useState(
    initialTaxLabelStatus || taxLabelStatusEnum.Exclusive
  );
  const [amount, setAmount] = useState<number | null>(initialAmount ?? null);

  const options = libraryDisbursements.map((ld) => ({
    value: ld.name,
    amount: ld.amount,
  }));

  useEffect(() => {
    autoFocus && onAutoFocus && onAutoFocus();
  }, [autoFocus, onAutoFocus]);

  useEffect(() => {
    setAmount(initialAmount);
  }, [initialAmount]);

  // Prop could be changed by parent
  useEffect(() => {
    setVatStatus(initialTaxLabelStatus);
  }, [initialTaxLabelStatus]);

  return (
    <div className={disbursementContainerClassname}>
      <div className={nameInputClassname}>
        <AutoComplete
          autoFocus={autoFocus}
          options={options}
          placeholder="Name"
          defaultValue={name}
          showSearch
          filterOption={(inputValue, option) =>
            option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
          }
          onBlur={(e) => {
            if ((e.target as HTMLInputElement).value !== name) {
              onUpdate({
                id,
                name: (e.target as HTMLInputElement).value || "",
              });
            }
          }}
          onSelect={(value, option) => {
            if (option.value !== name) {
              onUpdate({
                id,
                name: option.value,
                amount: option.amount,
              });
            }
          }}
        />
        <Radio.Group
          value={taxLabelStatus}
          onChange={(e) => {
            setVatStatus(e.target.value);
            onUpdate({ id, vatStatus: e.target.value });
          }}
        >
          <Radio value={taxLabelStatusEnum.Exclusive}>{taxLabel} exclusive</Radio>
          <Radio value={taxLabelStatusEnum.Inclusive}>{taxLabel} inclusive</Radio>
          <Radio value={taxLabelStatusEnum.Exempt}>{taxLabel} exempt</Radio>
        </Radio.Group>
      </div>
      <div className={amountInputClassname}>
        <PriceInput
          currencySymbol={currencySymbol}
          placeholder="Value"
          min={0}
          value={amount || 0}
          onChange={(newValue) => {
            if (newValue !== amount) {
              setAmount(newValue);
              onUpdate({
                id,
                amount: newValue,
              });
            }
          }}
          step={0.1}
        />
        {!excludeTax && (
          <div>
            <span className={vatLabeClassname}>
              {taxLabelStatus === taxLabelStatusEnum.Exempt ? "0" : taxLevelPercent}% {taxLabel}:{" "}
            </span>
            {calculateVatText(amount || 0, taxLabelStatus, taxLevelPercent, currencySymbol)}
          </div>
        )}
      </div>
      <Button icon={Icons.Delete} shape="circle" onClick={() => onDelete(id)} type="link" />
    </div>
  );
};

function calculateVatText(
  value: number,
  taxLabelStatus: taxLabelStatusEnum,
  vatLevel: number,
  currencySymbol: string
) {
  if (taxLabelStatus === taxLabelStatusEnum.Exempt) {
    return "Exempt";
  } else if (taxLabelStatus === taxLabelStatusEnum.Inclusive) {
    return "Included";
  } else {
    const vatAmount = (vatLevel / 100) * value;
    const roundedVatAmount = Math.round(vatAmount * 100) / 100;
    return `${currencySymbol}${roundedVatAmount.toFixed(2)}`;
  }
}
